import { useEffect } from "react";

interface WatermarkOverlayProps {
  email: string;
  ip?: string;
}

const WATERMARK_INVISIBLE_ID = "__wm_inv";
const WATERMARK_VISIBLE_ID = "__wm_vis";

export default function WatermarkOverlay({ email, ip }: WatermarkOverlayProps) {
  useEffect(() => {
    function createInvisibleLayer(): HTMLDivElement {
      const el = document.createElement("div");
      el.id = WATERMARK_INVISIBLE_ID;
      el.setAttribute("aria-hidden", "true");
      Object.assign(el.style, {
        position: "fixed",
        inset: "0",
        zIndex: "9998",
        pointerEvents: "none",
        userSelect: "none",
        opacity: "0.008",
        overflow: "hidden",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, 320px)",
        gridTemplateRows: "repeat(auto-fill, 120px)",
        alignContent: "start",
        gap: "0",
      });

      const ts = new Date().toISOString().slice(0, 16);
      const label = [email, ip, ts].filter(Boolean).join(" | ");
      for (let i = 0; i < 200; i++) {
        const span = document.createElement("span");
        span.textContent = label;
        Object.assign(span.style, {
          fontSize: "10px",
          fontFamily: "monospace",
          color: "#d0d0d0",
          transform: `rotate(-30deg)`,
          whiteSpace: "nowrap",
          padding: "20px",
        });
        el.appendChild(span);
      }
      return el;
    }

    function createVisibleLayer(): HTMLDivElement {
      const el = document.createElement("div");
      el.id = WATERMARK_VISIBLE_ID;
      el.setAttribute("aria-hidden", "true");
      Object.assign(el.style, {
        position: "fixed",
        bottom: "0",
        left: "0",
        right: "0",
        zIndex: "9999",
        pointerEvents: "none",
        userSelect: "none",
        opacity: "0.35",
        background: "linear-gradient(transparent, rgba(0,0,0,0.6))",
        padding: "6px 16px",
        textAlign: "center",
        fontSize: "11px",
        fontFamily: "monospace",
        color: "#ffffff",
        letterSpacing: "0.5px",
      });
      el.textContent = `CONFIDENTIAL — Viewer ${email}${ip ? ` (${ip})` : ""}`;
      return el;
    }

    document.getElementById(WATERMARK_INVISIBLE_ID)?.remove();
    document.getElementById(WATERMARK_VISIBLE_ID)?.remove();

    document.body.appendChild(createInvisibleLayer());
    document.body.appendChild(createVisibleLayer());

    const observer = new MutationObserver(() => {
      if (!document.getElementById(WATERMARK_INVISIBLE_ID)) {
        document.body.appendChild(createInvisibleLayer());
      }
      if (!document.getElementById(WATERMARK_VISIBLE_ID)) {
        document.body.appendChild(createVisibleLayer());
      }
    });

    observer.observe(document.body, { childList: true, subtree: false });

    return () => {
      observer.disconnect();
      document.getElementById(WATERMARK_INVISIBLE_ID)?.remove();
      document.getElementById(WATERMARK_VISIBLE_ID)?.remove();
    };
  }, [email, ip]);

  return null;
}
